import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { Injectable } from '@angular/core';
import * as moment from 'moment';

@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  
  format(date: Date, displayFormat: Object): string {
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    
    if (displayFormat === 'input') {
      let day: string = ('0' + date.getDate().toString()).slice(-2);
      let month = months[date.getMonth()];
      let year = date.getFullYear();
      return `${day} ${month} ${year}`;
    }
    return date.toDateString();
  }

  parse(value: any): Date | null {
    if (typeof value === 'string' && value) {
      // Handle DD-MM-YYYY format (your app's primary format)
      const ddMmYyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
      const ddMmYyyyMatch = value.match(ddMmYyyyPattern);
      
      if (ddMmYyyyMatch) {
        const day = parseInt(ddMmYyyyMatch[1], 10);
        const month = parseInt(ddMmYyyyMatch[2], 10) - 1; // Month is 0-indexed
        const year = parseInt(ddMmYyyyMatch[3], 10);
        
        // Validate the date components
        if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year > 1900) {
          const date = new Date(year, month, day, 12, 0, 0);
          // Double-check that the date was created correctly (handles edge cases like Feb 30)
          if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
            // console.log(`[AppDateAdapter] Successfully parsed DD-MM-YYYY: ${value} -> ${date.toISOString()}`);
            return date;
          }
        }
        // console.warn(`[AppDateAdapter] Invalid DD-MM-YYYY date: ${value} (day: ${day}, month: ${month}, year: ${year})`);
      }
      
      // Parse your format: "15 Jan 2024"
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const parts = value.trim().split(' ');
      
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const monthIndex = months.indexOf(parts[1]);
        const year = parseInt(parts[2], 10);
        
        if (day && monthIndex >= 0 && year) {
          // Create date at noon to avoid timezone issues
          const date = new Date(year, monthIndex, day, 12, 0, 0);
          // console.log(`[AppDateAdapter] Successfully parsed DD MMM YYYY: ${value} -> ${date.toISOString()}`);
          return date;
        }
      }
      
      // console.log(`[AppDateAdapter] Falling back to super.parse for: ${value}`);
      // Fallback to default parsing
      return super.parse(value);
    }
    return super.parse(value);
  }

  // THIS IS THE CRITICAL FIX - Override deserialize
  deserialize(value: any): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    // console.log(`[AppDateAdapter] deserialize called with:`, value, typeof value);
    
    if (typeof value === 'string') {
      // First, check if it's DD-MM-YYYY format (your app's format)
      const ddMmYyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
      const ddMmYyyyMatch = value.match(ddMmYyyyPattern);
      
      if (ddMmYyyyMatch) {
        const day = parseInt(ddMmYyyyMatch[1], 10);
        const month = parseInt(ddMmYyyyMatch[2], 10) - 1; // 0-indexed
        const year = parseInt(ddMmYyyyMatch[3], 10);
        
        // Validate the date components
        if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year > 1900) {
          const date = new Date(year, month, day, 12, 0, 0);
          // Validate that the date is correct
          if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
            // console.log(`[AppDateAdapter] Successfully deserialized DD-MM-YYYY: ${value} -> ${date.toISOString()}`);
            return date;
          }
        }
        
        // If we get here, the date was invalid, log for debugging
        // console.warn('Invalid DD-MM-YYYY date:', value, 'day:', day, 'month:', month, 'year:', year);
      }
      
      // Check if it's an ISO string (with 'T')
      if (value.indexOf('T') > -1) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const result = new Date(
            date.getUTCFullYear(), 
            date.getUTCMonth(), 
            date.getUTCDate(), 
            12, 0, 0
          );
          // console.log(`[AppDateAdapter] Successfully deserialized ISO string: ${value} -> ${result.toISOString()}`);
          return result;
        }
      }
      
      // Try parsing as your custom format (DD MMM YYYY)
      const parsedDate = this.parse(value);
      if (parsedDate) {
        // console.log(`[AppDateAdapter] Successfully parsed via parse method: ${value} -> ${parsedDate.toISOString()}`);
        return parsedDate;
      }
      
      // Last resort: try to parse with moment.js if available
      if (typeof moment !== 'undefined') {
        try {
          const momentDate = moment(value, 'DD-MM-YYYY', true);
          if (momentDate.isValid()) {
            const result = momentDate.toDate();
            // console.log(`[AppDateAdapter] Successfully parsed via moment: ${value} -> ${result.toISOString()}`);
            return result;
          }
        } catch (e) {
          // console.warn('Moment parsing failed for:', value, e);
        }
      }
      
      // console.warn(`[AppDateAdapter] Failed to deserialize: ${value}`);
    }
    
    if (typeof value === 'number') {
      const result = new Date(value);
      // console.log(`[AppDateAdapter] Successfully deserialized number: ${value} -> ${result.toISOString()}`);
      return result;
    }
    
    if (value instanceof Date) {
      const result = new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        12, 0, 0
      );
      // console.log(`[AppDateAdapter] Successfully deserialized Date object: ${value.toISOString()} -> ${result.toISOString()}`);
      return result;
    }
    
    // console.log(`[AppDateAdapter] Falling back to super.deserialize for:`, value);
    return super.deserialize(value);
  }

  // Override createDate to ensure dates are always created at noon
  createDate(year: number, month: number, date: number): Date {
    // Create date at noon to prevent timezone issues
    return new Date(year, month, date, 12, 0, 0);
  }
}

export const APP_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'numeric' },
    dateA11yLabel: { year: 'numeric', month: 'short', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'short' },
  }
};